import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const WarumBetrubstDuDichViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Warum betrübst du dich BWV 516
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV516/BWV-516/BWV-516-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Warum betrübst du dich BWV 516.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-10038">
      <Navbar data-id="element-10039" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-10040">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-10041">
          <img src="https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=400&h=300&fit=crop" alt="Warum betrübst du dich" className="w-full h-64 md:h-80 object-cover" data-id="element-10042" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-10043" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-10044">
            <div className="max-w-7xl mx-auto" data-id="element-10045">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-10046">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-10047" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-10048">
                Warum betrübst du dich
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-10049">BWV 516, Aria</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-10050">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-10051">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-10052">
          <div className="flex items-center space-x-2" data-id="element-10053">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-10054">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-10055" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10056">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-10057">
                  <ZoomInIcon className="h-5 w-5" data-id="element-10058" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-10059">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-10060">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-10061" />
              <span className="hidden sm:inline" data-id="element-10062">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-10063">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-10064" />
              <span className="hidden sm:inline" data-id="element-10065">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-10066">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-10067">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-10068">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Warum betrübst du dich Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-10069" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-10070">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10071">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-10072">
            <div data-id="element-10073">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10074">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-10075">
                <div data-id="element-10076">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10077">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10078">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-10079">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10080">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10081">
                    18th Century
                  </dd>
                </div>
                <div data-id="element-10082">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10083">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10084">
                    Aria
                  </dd>
                </div>
                <div data-id="element-10085">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10086">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10087">
                    BWV 516
                  </dd>
                </div>
                <div data-id="element-10088">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-10089">
                    Text
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10090">
                    "Warum betrübst du dich" (Why do you trouble yourself)
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-10091">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-10092">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10093">
                This contemplative aria sets the text "Warum betrübst du dich"
                (Why do you trouble yourself), a question that speaks to themes
                of faith, comfort, and trust in divine providence. Bach's
                setting demonstrates his mastery of text-music relationships,
                with the melodic line reflecting the questioning nature of the
                text while the harmonic progressions provide reassurance and
                resolution. The piece exemplifies the Lutheran tradition of
                sacred song, where music serves to illuminate and deepen the
                meaning of the text. The aria's gentle character and accessible
                melodic writing make it suitable for both devotional and
                pedagogical purposes, while its sophisticated harmonic language
                rewards careful study and interpretation.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-10094" />
    </div>;
};